import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Block } from '@core/models/block/block.model';
import { GlobalService } from '@core/services/common/global.service';
import { camelCase } from 'lodash';
import { getStyles } from '@shared/helpers/block-helpers';
import { SubSink } from '@core/utils/subsink';
import { EditorService } from '@app/modules/editor/services/editor.service';

interface Slide {
  leftSide: boolean;
  rightSide: boolean;
  isSelected: boolean;
  action: string;
}

type BlockSlide = Block | Slide;

// **
// @description Component structure is inspired by ng-simple-slideshow
//              see more https://www.npmjs.com/package/ng-simple-slideshow or
//              check demo page https://ng-simple-slideshow.firebaseapp.com/
// */
@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SliderComponent
  implements OnInit, AfterViewInit, DoCheck, OnDestroy {
  subSink = new SubSink();

  slideIndex = -1;
  lazyLoadRest = false;
  slides: BlockSlide[];
  leftArrow: Block;
  rightArrow: Block;
  @Input() specialBlock: Block;
  @ViewChild('container') container: ElementRef;
  private _noLoop = false;
  private _autoPlay = true;
  private _stopAutoPlayOnSlide = true;
  private _autoplayIntervalId: any;
  private _autoPlayInterval = 5000;
  blockHasAspectRatio = false;
  animationDurationL = 500; // lub inna wartość

  constructor(
    private _renderer: Renderer2,
    private _ngZone: NgZone,
    private _cdRef: ChangeDetectorRef,
    private globalService: GlobalService,
    private editorService: EditorService
  ) {
  }

  private _height = 0;

  get height() {
    return this._height;
  }

  private _animationDuration = 1;

  get animationDuration() {
    return this._animationDuration;
  }

  ngOnInit(): void {
    const [slidesContainer, leftArrow, rightArrow] = this.specialBlock.children;

    this.slides = slidesContainer.children;

    this.rightArrow = rightArrow;
    this.leftArrow = leftArrow;

    const {height, animationDelay, animationDuration} =
      this.specialBlock.settings;

    this.editorService.settingsSlider = this.specialBlock.settings;

    this._height = height;
    this._autoPlayInterval = animationDelay * 1000;
    this._animationDuration = animationDuration * 1000;

    this.subSink.sink = this.globalService.mediaQuery.subscribe((mq) => {
      const querySnakeCase = camelCase(mq.query);
      const s = getStyles(this.specialBlock.styles, querySnakeCase).basic;
      this.blockHasAspectRatio = !!s.aspectRatio && s.aspectRatio !== 'auto';
    });
    setTimeout(() => {
      this.lazyLoadRest = true;
    }, 500);
  }

  ngAfterViewInit(): void {
    const {animationType} = this.specialBlock.settings;
    this._renderer.addClass(
      this.container.nativeElement,
      animationType || 'fadeIn'
    );
  }

  ngDoCheck(): void {
    this.setSlides();
    this.handleAutoPlay();
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();

    try {
      if (this._autoplayIntervalId) {
        this._ngZone.runOutsideAngular(() =>
          clearInterval(this._autoplayIntervalId)
        );
        this._autoplayIntervalId = null;
      }
    } catch (error) {
      console.warn(
        'Autoplay cancel error caught in ng-simple-slideshow OnDestroy:',
        error
      );
    }
  }

  /**
   * @description this is the function that should be called to make the slides change.
   *              indexDirection to move back is -1, to move forward is 1, and to stay in place is 0.
   *              0 is taken into account for failed swipes
   */
  onSlide(indexDirection: number, isSwipe?: boolean): void {
    this.handleAutoPlay(this._stopAutoPlayOnSlide);
    this.slide(indexDirection, isSwipe);
  }

  /**
   * @description Set the new slide index, then make the transition happen.
   */
  private slide(indexDirection: number, isSwipe?: boolean): void {
    const oldIndex = this.slideIndex;

    if (this.setSlideIndex(indexDirection)) {
      if (indexDirection === 1) {
        this.slideRight(oldIndex, isSwipe);
      } else {
        this.slideLeft(oldIndex, isSwipe);
      }

      this.slides[oldIndex].isSelected = false;
      this.slides[this.slideIndex].isSelected = true;
    }

    this._cdRef.detectChanges();
  }

  /**
   * @description This is just treating the url array like a circular list.
   */
  private setSlideIndex(indexDirection: number): boolean {
    let willChange = true;
    this.slideIndex += indexDirection;

    if (this.slideIndex < 0) {
      if (this._noLoop) {
        this.slideIndex -= indexDirection;
        willChange = false;
      } else {
        this.slideIndex = this.slides.length - 1;
      }
    } else if (this.slideIndex >= this.slides.length) {
      if (this._noLoop) {
        this.slideIndex -= indexDirection;
        willChange = false;
      } else {
        this.slideIndex = 0;
      }
    }

    return willChange;
  }

  /**
   * @description Start or stop _autoPlay, don't do it at all server side
   */
  private handleAutoPlay(stopAutoPlay?: boolean): void {
    if (stopAutoPlay === true || this._autoPlay === false) {
      if (this._autoplayIntervalId) {
        this._ngZone.runOutsideAngular(() =>
          clearInterval(this._autoplayIntervalId)
        );
        this._autoplayIntervalId = null;
      }
    } else if (!this._autoplayIntervalId) {
      this._ngZone.runOutsideAngular(() => {
        this._autoplayIntervalId = setInterval(() => {
          if (this.slides[this.slideIndex]) {
            this._ngZone.run(() => this.slide(1));
          }
        }, this._autoPlayInterval);
      });
    }
  }

  /**
   * @description get the index for the slide to the left of the new slide
   */
  private getLeftSideIndex(i?: number): number {
    if (i === undefined) {
      i = this.slideIndex;
    }

    if (--i < 0) {
      i = this.slides.length - 1;
    }

    return i;
  }

  /**
   * @description get the index for the slide to the right of the new slide
   */
  private getRightSideIndex(i?: number): number {
    if (i === undefined) {
      i = this.slideIndex;
    }

    if (++i >= this.slides.length) {
      i = 0;
    }

    return i;
  }

  /**
   * @description This function handles the variables to move the CSS classes around accordingly.
   *              In order to correctly handle animations, the new slide as well as the slides to
   *              the left and right are assigned classes.
   */
  private slideLeft(oldIndex: number, isSwipe?: boolean): void {
    this.slides[this.getLeftSideIndex(oldIndex)].leftSide = false;
    this.slides[oldIndex].leftSide = true;
    this.slides[oldIndex].action = 'slideOutLeft';
    this.slides[this.slideIndex].rightSide = false;
    this.slides[this.getRightSideIndex()].rightSide = true;
    this.slides[this.slideIndex].action = 'slideInRight';
  }

  /**
   * @description This function handles the variables to move the CSS classes around accordingly.
   *              In order to correctly handle animations, the new slide as well as the slides to
   *              the left and right are assigned classes.
   */
  private slideRight(oldIndex: number, isSwipe?: boolean): void {
    this.slides[this.getRightSideIndex(oldIndex)].rightSide = false;
    this.slides[oldIndex].rightSide = true;
    this.slides[oldIndex].action = 'slideOutRight';
    this.slides[this.slideIndex].leftSide = false;
    this.slides[this.getLeftSideIndex()].leftSide = true;
    this.slides[this.slideIndex].action = 'slideInLeft';
  }

  private setSlides(): void {
    if (this.slideIndex === -1) {
      this.slideIndex = 0;
    }

    this.slides[this.slideIndex].isSelected = true;
    this._cdRef.detectChanges();
  }
}
